import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach } from '@/features/legends/editor/collections'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import type { QuestionEditorViewModelBase } from '@/features/legends/questions/QuestionEditorViewModelBase'
import { tapAction, toStream } from '@/utils/mobx-util'
import { validateAll, validateElements } from '@/utils/validx-util'
import { TemplateEditorViewModel } from './TemplateEditorViewModel'
import type { Template } from './templates'

/**
 * The legend's scoping templates.
 */
export class TemplatesBuilderViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<TemplatesBuilderViewModel> = {
    templates: [validateElements('There are invalid templates.')],
  }

  readonly templates = observable.array<TemplateEditorViewModel>([], {
    deep: false,
  })
  @observable.ref accessor selectedTemplateVm: TemplateEditorViewModel | null =
    null

  readonly validation = validationContext(this, this._schema)

  /**
   * Keeps one mapping per question in every template as questions are added
   * and removed.
   */
  private readonly _mappingUpdates$ = toStream(() => this._getQuestions()).pipe(
    tapAction((questions) =>
      this.templates.forEach((t) => t.updateMappings(questions)),
    ),
  )

  /**
   * @param dependencies
   * @param _getQuestions Every question of the legend.
   */
  constructor(
    dependencies: LegendPartDependencies,
    private readonly _getQuestions: () => QuestionEditorViewModelBase[],
  ) {
    super(dependencies)
    this.lifecycle.manage(this._mappingUpdates$)
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.templates))
  }

  /**
   * The templates and their mappings, so that their IDs stay unique.
   */
  @computed
  get entityDescriptors(): EntityDescriptor[] {
    return this.templates.flatMap((template) => [
      { type: 'Template', entity: template },
      ...template.mappings.map((mapping) => ({
        type: 'Mapping',
        entity: mapping,
      })),
    ])
  }

  @computed
  private get _json(): Template[] {
    return this.templates.map((t) => t.toJSON())
  }

  /**
   * Adds a template with a mapping for each question, and selects it.
   */
  @action.bound
  add() {
    const template = new TemplateEditorViewModel(this.dependencies)
    template.updateMappings(this._getQuestions())
    this.templates.push(template)
    this.edit(template)
  }

  /**
   * Selects the template.
   */
  @action.bound
  edit(template: TemplateEditorViewModel) {
    validateAll(this)
    this.selectedTemplateVm = template
  }

  /**
   * Removes the selected template.
   */
  @action.bound
  remove() {
    if (!this.selectedTemplateVm) {
      return
    }
    this.templates.remove(this.selectedTemplateVm)
    this.selectedTemplateVm = null
    validateAll(this)
  }

  /**
   * Sets the templates from the legend document.
   */
  @action
  hydrate(templates: Template[] | undefined) {
    const questions = this._getQuestions()
    this.templates.replace(
      (templates ?? []).map((data) => {
        const template = new TemplateEditorViewModel(this.dependencies)
        template.hydrate(data, questions)
        return template
      }),
    )
  }

  /**
   * Shows the analyzer's errors on the templates.
   */
  receiveErrors(descriptor: ErrorDescriptor | null | undefined) {
    addGenericErrorForProperty(
      this,
      descriptor,
      'templates',
      'There are invalid templates.',
    )
    receiveErrors(this.templates, descriptor?.inner)
  }

  /**
   * The templates as the legend document has them.
   */
  toJSON(): Template[] {
    return this._json
  }
}
